import { formatDate, formatTime, toBerlinDateKey } from '@brimo/shared';
import { parseTimeInput } from '../../lib/time-input';

/**
 * Calendar helpers of the module (Europe/Berlin, no date library): day keys, weekday names, month grids and
 * the compound texts of the screens („Do 17.09.2026 · 16:00", „Mi 16.09. · 17:15 – 18:00").
 */

const TIME_ZONE = 'Europe/Berlin';

const weekdayFormat = new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: TIME_ZONE });
const monthFormat = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric', timeZone: TIME_ZONE });

/** The weekday labels of the screens (Beratung 08: Mo … So). */
export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;
/** Einstellungen 14 writes the full names. */
export const WEEKDAY_LONG = [
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
] as const;

/** "Do" for a point in time (Intl gives "Do." in German). */
export function weekdayShort(value: Date | string): string {
  return weekdayFormat.format(toDate(value)).replace('.', '');
}

/** "September 2026" */
export function monthTitle(dayKey: string): string {
  return monthFormat.format(dayKeyToDate(dayKey));
}

/** „Do 17.09.2026 · 16:00" — the panel title of Beratung 02/03. */
export function formatPanelTitle(value: Date | string): string {
  return `${weekdayShort(value)} ${formatDate(value)} · ${formatTime(value)}`;
}

/** „Mi 16.09. · 17:15 – 18:00" — the alternatives of Beratung 03. */
export function formatSlotRange(startsAt: string, endsAt: string): string {
  return `${weekdayShort(startsAt)} ${shortDate(startsAt)} · ${formatTime(startsAt)} – ${formatTime(endsAt)}`;
}

/** „16:00 – 16:45" */
export function formatTimeRange(startsAt: string, endsAt: string): string {
  return `${formatTime(startsAt)} – ${formatTime(endsAt)}`;
}

/** "16.09." */
export function shortDate(value: Date | string): string {
  return formatDate(value).slice(0, 6);
}

/** „Donnerstag, 17. September" — the day heading of the slot picker. */
export function formatDayHeading(dayKey: string): string {
  const date = dayKeyToDate(dayKey);
  const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long', timeZone: 'UTC' }).format(date);
  const dayMonth = new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(date);
  return `${weekday}, ${dayMonth}`;
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/** A day key ('2026-09-17') as a Date at UTC midnight; the calendar arithmetic runs on those. */
export function dayKeyToDate(dayKey: string): Date {
  const [year = 1970, month = 1, day = 1] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

export function dateToDayKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(dayKey: string, days: number): string {
  const date = dayKeyToDate(dayKey);
  date.setUTCDate(date.getUTCDate() + days);
  return dateToDayKey(date);
}

export function addMonths(dayKey: string, months: number): string {
  const date = dayKeyToDate(dayKey);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  return dateToDayKey(date);
}

/** ISO weekday 1 (Monday) … 7 (Sunday) of a day key. */
export function isoWeekday(dayKey: string): number {
  const day = dayKeyToDate(dayKey).getUTCDay();
  return day === 0 ? 7 : day;
}

/** The Monday of the week that holds `dayKey`. */
export function startOfWeek(dayKey: string): string {
  return addDays(dayKey, 1 - isoWeekday(dayKey));
}

export function todayKey(now: Date = new Date()): string {
  return toBerlinDateKey(now);
}

/** The instant (ISO) of a Berlin wall-clock time on a day key, e.g. `berlinInstant('2026-09-17', '16:00')`. */
export function berlinInstant(dayKey: string, time = '00:00'): string {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const naive = Date.UTC(
    Number(dayKey.slice(0, 4)),
    Number(dayKey.slice(5, 7)) - 1,
    Number(dayKey.slice(8, 10)),
    hours,
    minutes,
  );
  // The offset of that moment in Berlin (CET/CEST); the naive UTC value is off by exactly this offset.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    timeZoneName: 'longOffset',
  }).formatToParts(new Date(naive));
  const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT+00:00';
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name);
  const sign = match?.[1] === '-' ? -1 : 1;
  const offsetMinutes = match ? sign * (Number(match[2]) * 60 + Number(match[3])) : 0;
  return new Date(naive - offsetMinutes * 60_000).toISOString();
}

/** The weeks of a month (Mo–So rows), padded with the neighbouring months' days. */
export function monthGrid(dayKey: string): Array<Array<{ key: string; inMonth: boolean }>> {
  const first = `${dayKey.slice(0, 7)}-01`;
  const month = dayKey.slice(0, 7);
  let cursor = startOfWeek(first);
  const weeks: Array<Array<{ key: string; inMonth: boolean }>> = [];
  do {
    const week: Array<{ key: string; inMonth: boolean }> = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({ key: cursor, inMonth: cursor.startsWith(month) });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  } while (cursor.startsWith(month));
  return weeks;
}

/** „17." — the day number of a day key. */
export function dayNumber(dayKey: string): number {
  return Number(dayKey.slice(8, 10));
}

/** Compares "HH:MM" strings. */
export function compareTimes(a: string, b: string): number {
  return a.localeCompare(b);
}

/** Normalises what a user typed into a time field ("9:00", "0900", "9") to "HH:MM"; null when unreadable. */
export function normalizeTime(text: string): string | null {
  return parseTimeInput(text);
}

/** „vor 2 Minuten", „vor 3 Stunden 12 Minuten", „gestern", „12.09.2026" — relative times of the connection cards. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const diff = Math.max(0, now.getTime() - new Date(iso).getTime());
  const seconds = Math.round(diff / 1000);
  if (seconds < 60) return `vor ${seconds} Sekunden`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `vor ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    const hourText = `${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
    return rest > 0 ? `vor ${hourText} ${rest} ${rest === 1 ? 'Minute' : 'Minuten'}` : `vor ${hourText}`;
  }
  const days = Math.floor(hours / 24);
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  return formatDate(iso);
}

/** „heute, 11:02" or „12.09.2026, 08:14" (E12 „Zuletzt erreichbar"). */
export function dayTime(iso: string, now: Date = new Date()): string {
  return toBerlinDateKey(iso) === toBerlinDateKey(now)
    ? `heute, ${formatTime(iso)}`
    : `${formatDate(iso)}, ${formatTime(iso)}`;
}

/** „0:48" — the QR countdown of Einstellungen 08. */
export function formatCountdown(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
